import React, {useState, useEffect} from 'react';
import WebflowSlider from '@packages/systems/design-system/WebflowSlider';
import {FlexRow} from '@packages/systems/design-system/Layout';
import {styled, emotionStyled} from '@packages/utilities/styled';

import {setType, type Type, TypeImplementation} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import {TNumber, ENumber} from '@packages/systems/wfdl-ast';

type Props = {
  min: number;
  max: number;
  step: number;
  inputType: Type;
};

export const SliderWithUnit = ({min, max, step, inputType}: Props) =>
  TypeImplementation.Alias({
    type: TNumber,
    render: renderSliderWithUnit({min, max, step, inputType}),
  });

const SliderBar = styled(WebflowSlider)(({theme: {gutters}}) => ({
  flexGrow: 1,
  marginLeft: gutters.normal,
  marginRight: gutters.normal,
}));

const InputWrapper = emotionStyled('div')({
  width: 53,
});

const renderSliderWithUnit =
  ({min, max, step, inputType}: Props) =>
  ({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: TypeImplementation.ExpressionEditorDispatch;
  }) => {
    const [value, setValue] = useState(
      ContextualExpressions.getNumber(stripAnn(expr))
    );

    if (value !== null) {
      useEffect(() => {
        setValue(ContextualExpressions.getNumber(stripAnn(expr)));
      }, [expr]);

      const onChange = (numberValue: number) => {
        if (numberValue !== value) {
          dispatch(updateExpressionAction(ENumber(numberValue)));
        }
      };

      const newExpr = setType(expr, inputType);

      return (
        <FlexRow>
          <SliderBar
            data-automation-id="slider-type-bar"
            disabled={false}
            value={value || min}
            onChange={onChange}
            min={min}
            max={max}
            step={step}
          />
          <InputWrapper>
            <ExpressionEditor dispatch={dispatch} expression={newExpr} />
          </InputWrapper>
        </FlexRow>
      );
    }

    return <ExpressionEditor dispatch={dispatch} expression={expr} />;
  };
